import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import jsPDF from 'jspdf';
import Logo from '../assets/images/logo.svg';
import { AGB_URL, PRIVACY_URL } from '../constants/links';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const API_BASE_URL = 'https://r2cok49t3g.execute-api.eu-central-1.amazonaws.com/prod';
const API_KEY = 'xVub93q3wq3dyZ0TgqyC24FwsK53UVbM7FOv171v';

export default function Nachreichen() {
  const { auditId } = useParams();
  const query = useQuery();
  const accessKey = query.get('access_key');
  const [schemaData, setSchemaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contractFiles, setContractFiles] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const contractInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSchema = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // // TEMPORARY MOCK - remove after testing
        // const mockResponse = {
        //   missing_contract: true, // Test: true, false
        //   questions: [
        //     {
        //       question_key: 'contract_type',
        //       question: 'Wie viele Quadratmeter hat deine Wohnung?'
        //     },
        //     {
        //       question_key: 'contract_date',
        //       question: 'Hat dein Vermieter die Änderung der Vorauszahlung mit dir festgehalten?'
        //     }
        //   ]
        // };
        // if (!isMounted) return;
        // setSchemaData(mockResponse);
        // 
        // // Initialize answers object with empty strings for each question
        // const mockInitialAnswers = {};
        // if (mockResponse.questions && Array.isArray(mockResponse.questions)) {
        //   mockResponse.questions.forEach(q => {
        //     mockInitialAnswers[q.question_key] = '';
        //   });
        // }
        // setAnswers(mockInitialAnswers);
        // if (isMounted) setLoading(false);
        // return;
        // // END MOCK
        
        const url = `${API_BASE_URL}/audits/information_follow_up_schema/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${API_KEY}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!isMounted) return;
        
        const data = response.data || {};
        setSchemaData(data);
        
        // Initialize answers object with empty strings for each question
        const initialAnswers = {};
        if (data.questions && Array.isArray(data.questions)) {
          data.questions.forEach(q => {
            initialAnswers[q.question_key] = '';
          });
        }
        setAnswers(initialAnswers);
      } catch (err) {
        if (!isMounted) return;
        setError(err?.response?.data || err?.message || 'Fehler beim Laden der Informationen');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSchema();

    return () => {
      isMounted = false;
    };
  }, [auditId, accessKey]);

  // Redirect back to status if there's nothing to submit
  useEffect(() => {
    if (!schemaData || loading) return;
    
    const hasMissingContract = schemaData.missing_contract === true;
    const hasQuestions = schemaData.questions && Array.isArray(schemaData.questions) && schemaData.questions.length > 0;
    
    if (!hasMissingContract && !hasQuestions) {
      // Nothing to submit, redirect back to status
      const statusUrl = `/status/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
      window.location.assign(statusUrl);
    }
  }, [schemaData, loading, auditId, accessKey]);

  // Helper function to load image from file
  const loadImageFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Convert a single image file to PDF
  const convertImageToPdf = async (imageFile) => {
    try {
      // First compress the image if needed
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg'
      };
      
      let processedImage = imageFile;
      if (imageFile.type.startsWith('image/') || 
          imageFile.name.toLowerCase().endsWith('.heic') || 
          imageFile.name.toLowerCase().endsWith('.heif')) {
        try {
          processedImage = await imageCompression(imageFile, options);
        } catch (error) {
          console.error('Error compressing image:', error);
          // Continue with original file if compression fails
        }
      }

      // Load the image
      const img = await loadImageFromFile(processedImage);
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: img.width > img.height ? 'landscape' : 'portrait',
        unit: 'mm'
      });

      // Calculate dimensions to fit the page while maintaining aspect ratio
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgAspectRatio = img.width / img.height;
      const pdfAspectRatio = pdfWidth / pdfHeight;

      let finalWidth, finalHeight;
      if (imgAspectRatio > pdfAspectRatio) {
        // Image is wider than PDF page
        finalWidth = pdfWidth;
        finalHeight = pdfWidth / imgAspectRatio;
      } else {
        // Image is taller than PDF page
        finalHeight = pdfHeight;
        finalWidth = pdfHeight * imgAspectRatio;
      }

      // Center the image on the page
      const x = (pdfWidth - finalWidth) / 2;
      const y = (pdfHeight - finalHeight) / 2;

      // Add image to PDF
      pdf.addImage(img, 'JPEG', x, y, finalWidth, finalHeight);

      // Convert PDF to blob and then to File
      const pdfBlob = pdf.output('blob');
      const fileName = imageFile.name.replace(/\.[^/.]+$/, '') + '.pdf';
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      return pdfFile;
    } catch (error) {
      console.error('Error converting image to PDF:', error);
      throw error;
    }
  };

  // Combine multiple image files into a single PDF
  const combineImagesToPdf = async (imageFiles) => {
    try {
      if (imageFiles.length === 0) {
        throw new Error('No images to combine');
      }

      if (imageFiles.length === 1) {
        // If only one image, just convert it to PDF
        return await convertImageToPdf(imageFiles[0]);
      }

      // Process all images first (compress if needed)
      const processedImages = await Promise.all(
        imageFiles.map(async (file) => {
          if (file.type.startsWith('image/') || 
              file.name.toLowerCase().endsWith('.heic') || 
              file.name.toLowerCase().endsWith('.heif')) {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              fileType: 'image/jpeg'
            };
            try {
              return await imageCompression(file, options);
            } catch (error) {
              console.error('Error compressing image:', error);
              return file;
            }
          }
          return file;
        })
      );

      // Load all images
      const images = await Promise.all(
        processedImages.map(file => loadImageFromFile(file))
      );

      // Create PDF
      const pdf = new jsPDF();
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Add each image as a new page
      images.forEach((img, index) => {
        if (index > 0) {
          pdf.addPage();
        }

        // Calculate dimensions to fit the page while maintaining aspect ratio
        const imgAspectRatio = img.width / img.height;
        const pdfAspectRatio = pdfWidth / pdfHeight;

        let finalWidth, finalHeight;
        if (imgAspectRatio > pdfAspectRatio) {
          finalWidth = pdfWidth;
          finalHeight = pdfWidth / imgAspectRatio;
        } else {
          finalHeight = pdfHeight;
          finalWidth = pdfHeight * imgAspectRatio;
        }

        // Center the image on the page
        const x = (pdfWidth - finalWidth) / 2;
        const y = (pdfHeight - finalHeight) / 2;

        // Add image to PDF
        pdf.addImage(img, 'JPEG', x, y, finalWidth, finalHeight);
      });

      // Convert PDF to blob and then to File
      const pdfBlob = pdf.output('blob');
      const baseFileName = imageFiles[0].name.replace(/\.[^/.]+$/, '');
      const fileName = baseFileName + '_combined.pdf';
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      return pdfFile;
    } catch (error) {
      console.error('Error combining images to PDF:', error);
      throw error;
    }
  };

  // Validate file type and size
  const validateFile = (file) => {
    const allowedTypes = ['.pdf', '.png', '.jpg', '.jpeg', '.heic', '.heif'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      alert(`Ungültiger Dateityp. Bitte laden Sie eine PDF, PNG, JPG oder HEIC Datei hoch.`);
      return false;
    }

    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    const totalSize = contractFiles.reduce((sum, f) => sum + f.size, 0) + file.size;

    if (totalSize > maxSize) {
      alert(`Die Gesamtgröße aller Dateien darf 100MB nicht überschreiten. Bitte entfernen Sie einige Dateien oder wählen Sie kleinere Dateien aus.`);
      return false;
    }

    return true;
  };

  const handleContractFileAdd = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      
      // Validate all files
      const validFiles = filesArray.filter(file => validateFile(file));
      if (validFiles.length === 0) return;
      
      // Separate images and PDFs
      const imageFiles = validFiles.filter(file => 
        file.type.startsWith('image/') || 
        file.name.toLowerCase().endsWith('.heic') || 
        file.name.toLowerCase().endsWith('.heif')
      );
      const pdfFiles = validFiles.filter(file => 
        file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      );
      
      if (imageFiles.length > 0) {
        // For images, keep them as images (don't convert yet) and add to the list
        // They will be converted to PDF when submitting
        setContractFiles(prev => {
          // Remove any existing combined PDFs (we'll recreate them on submit)
          const standalonePdfs = prev.filter(f => 
            f.type === 'application/pdf' && !f.name.includes('_combined')
          );
          // Keep existing images
          const existingImages = prev.filter(f => 
            f.type?.startsWith('image/') || 
            f.name.toLowerCase().endsWith('.heic') || 
            f.name.toLowerCase().endsWith('.heif')
          );
          // Add new images
          return [...standalonePdfs, ...existingImages, ...imageFiles, ...pdfFiles];
        });
      } else if (pdfFiles.length > 0) {
        // For PDFs, just add them
        setContractFiles(prev => [...prev, ...pdfFiles]);
      }
      
      // Reset the input so the same file can be selected again if needed
      e.target.value = '';
    }
  };

  const handleContractFileRemove = (index) => {
    setContractFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const filesArray = Array.from(files);
      
      // Validate all files
      const validFiles = filesArray.filter(file => validateFile(file));
      if (validFiles.length === 0) return;
      
      // Separate images and PDFs
      const imageFiles = validFiles.filter(file => 
        file.type.startsWith('image/') || 
        file.name.toLowerCase().endsWith('.heic') || 
        file.name.toLowerCase().endsWith('.heif')
      );
      const pdfFiles = validFiles.filter(file => 
        file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      );
      
      if (imageFiles.length > 0) {
        // For images, keep them as images (don't convert yet) and add to the list
        setContractFiles(prev => {
          const standalonePdfs = prev.filter(f => 
            f.type === 'application/pdf' && !f.name.includes('_combined')
          );
          const existingImages = prev.filter(f => 
            f.type?.startsWith('image/') || 
            f.name.toLowerCase().endsWith('.heic') || 
            f.name.toLowerCase().endsWith('.heif')
          );
          return [...standalonePdfs, ...existingImages, ...imageFiles, ...pdfFiles];
        });
      } else if (pdfFiles.length > 0) {
        // For PDFs, just add them
        setContractFiles(prev => [...prev, ...pdfFiles]);
      }
    }
  };

  const handleAnswerChange = (questionKey, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionKey]: value
    }));
  };

  const handleSubmit = async () => {
    // Validate that all questions are answered
    if (schemaData?.questions) {
      const unansweredQuestions = schemaData.questions.filter(q => !answers[q.question_key] || answers[q.question_key].trim() === '');
      if (unansweredQuestions.length > 0) {
        setSubmitError('Bitte beantworten Sie alle Fragen.');
        return;
      }
    }

    // Validate contract upload if required
    if (schemaData?.missing_contract && contractFiles.length === 0) {
      setSubmitError('Bitte laden Sie den Mietvertrag hoch.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let contractS3Filename = null;

      // Upload contract to S3 if needed
      if (schemaData?.missing_contract && contractFiles.length > 0) {
        setUploadingContract(true);
        try {
          // Separate images and PDFs
          const contractImages = contractFiles.filter(f => 
            f.type?.startsWith('image/') || 
            f.name.toLowerCase().endsWith('.heic') || 
            f.name.toLowerCase().endsWith('.heif')
          );
          const contractPdfs = contractFiles.filter(f => 
            f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
          );
          
          // Convert images to PDFs
          const contractProcessed = [];
          if (contractImages.length > 0) {
            // Combine all images into a single PDF
            const combinedPdf = await combineImagesToPdf(contractImages);
            contractProcessed.push(combinedPdf);
          }
          // Add standalone PDFs
          contractProcessed.push(...contractPdfs);
          
          // Get upload URLs for all files
          const uploadPromises = contractProcessed.map(async (file) => {
            const fileExtension = file.name.split('.').pop().toLowerCase().trim();
            const response = await axios.post(
            `${API_BASE_URL}/audits/get_upload?file_extension=${fileExtension}`,
            null,
            {
              headers: {
                Authorization: `Bearer ${API_KEY}`
              }
            }
          );
            return {
              file,
              uploadUrl: response.data.brunhild_presigned_put_url,
              filename: response.data.brunhild_s3_filename
            };
          });

          const uploadResults = await Promise.all(uploadPromises);
          
          // Upload all files to S3
          const s3UploadPromises = uploadResults.map(({ file, uploadUrl }) =>
            axios.put(uploadUrl, file, {
              headers: {
                'Content-Type': file.type || 'application/octet-stream'
              }
            })
          );
          
          await Promise.all(s3UploadPromises);
          
          // Use the first uploaded filename as missing_contract_s3_filename
          contractS3Filename = uploadResults[0]?.filename;
        } catch (uploadErr) {
          setUploadingContract(false);
          setIsSubmitting(false);
          setSubmitError('Fehler beim Hochladen des Mietvertrags. Bitte versuchen Sie es erneut.');
          return;
        }
        setUploadingContract(false);
      }

      // Prepare provided answers
      const providedAnswers = schemaData?.questions?.map(q => ({
        question_key: q.question_key,
        answer: answers[q.question_key] || ''
      })) || [];

      // Submit information follow-up
      const payload = {
        provided_answers: providedAnswers,
        start_audit: true
      };

      if (contractS3Filename) {
        payload.missing_contract_s3_filename = contractS3Filename;
      }

      const submitUrl = `${API_BASE_URL}/audits/provide_information_follow_up/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
      await axios.post(submitUrl, payload, {
        headers: {
          Authorization: `Bearer ${API_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      // Redirect to status page on success
      const statusUrl = `/status/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
      window.location.assign(statusUrl);
    } catch (err) {
      setIsSubmitting(false);
      setSubmitError('Die Übermittlung der Informationen war nicht erfolgreich');
    }
  };

  if (loading) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="upload-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#6B7280' }}>Lade...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <div className="upload-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px', maxWidth: '720px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '40px' }}>
              <img src={Logo} alt="Nebenkostenprofi Logo" style={{ height: '44px', width: 'auto' }} />
              <div style={{ fontSize: '1rem', color: '#000000' }}>Nebenkostenprofi.de</div>
            </div>
            <div style={{ color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', padding: '12px 16px', borderRadius: '8px' }}>
              <strong>Fehler:</strong> {String(error)}
            </div>
          </div>
        </div>
        <div style={{ width: '100%', background: '#F3F4F6', borderTop: '1px solid #E5E7EB', padding: '16px 24px' }}>
          <div style={{ maxWidth: '1024px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <a href={AGB_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>AGB</a>
              <span style={{ color: '#9CA3AF' }}>|</span>
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Datenschutzbestimmungen</a>
              <span style={{ color: '#9CA3AF' }}>|</span>
              <a href={'https://www.nebenkostenprofi.de'} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Startseite</a>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="upload-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px', maxWidth: '720px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '40px' }}>
            <img src={Logo} alt="Nebenkostenprofi Logo" style={{ height: '44px', width: 'auto' }} />
            <div style={{ fontSize: '1rem', color: '#000000' }}>Nebenkostenprofi.de</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
              Informationen nachreichen
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px' }}>
            {submitError && (
              <div style={{ color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', padding: '12px 16px', borderRadius: '8px' }}>
                <strong>Fehler:</strong> {submitError}
              </div>
            )}

            {/* Contract Upload Section */}
            {schemaData?.missing_contract && (
              <div style={{ 
                background: '#F0FDF4', 
                border: '2px solid #BBF7D0', 
                padding: '24px', 
                borderRadius: '12px',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{ 
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#15803d',
                    background: '#F0FDF4',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    1
                  </div>
                  <h3 style={{ 
                    margin: 0, 
                    color: '#15803d', 
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    textAlign: 'left'
                  }}>
                    Mietvertrag hochladen
                  </h3>
                </div>
                <div style={{ 
                  color: '#374151', 
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  marginBottom: '20px',
                  marginLeft: '44px',
                  textAlign: 'left'
                }}>
                  Für Ihre Prüfung benötigen wir Ihren Mietvertrag. Bitte laden Sie ihn hoch, damit die Prüfung fortgesetzt werden kann.
                </div>
                <div 
                  style={{ marginBottom: '16px' }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {contractFiles.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {contractFiles.map((file, index) => (
                        <div 
                          key={index}
                          style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '12px',
                      background: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                          }}
                        >
                          <span style={{ color: '#374151', fontSize: '0.95rem' }}>{file.name}</span>
                      <button
                            onClick={() => handleContractFileRemove(index)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#6B7280',
                          cursor: 'pointer',
                          fontSize: '18px',
                          padding: '0 8px'
                        }}
                      >
                        ×
                      </button>
                    </div>
                      ))}
                      <button
                        onClick={() => contractInputRef.current?.click()}
                        style={{
                          width: '100%',
                          background: '#FFFFFF',
                          border: '2px dashed #BBF7D0',
                          borderRadius: '14px',
                          padding: '16px',
                          cursor: 'pointer',
                          color: '#15803d',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          marginTop: '8px'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.borderColor = '#16A34A';
                          e.target.style.background = '#F0FDF4';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.borderColor = '#BBF7D0';
                          e.target.style.background = '#FFFFFF';
                        }}
                      >
                        Weitere Datei hinzufügen
                      </button>
                    </div>
                  ) : (
                    <div>
                      <button
                        onClick={() => contractInputRef.current?.click()}
                        style={{
                          width: '100%',
                          background: isDragging ? '#F0FDF4' : '#FFFFFF',
                          border: `2px dashed ${isDragging ? '#16A34A' : '#BBF7D0'}`,
                          borderRadius: '14px',
                          padding: '24px',
                          cursor: 'pointer',
                          color: '#15803d',
                          fontSize: '1rem',
                          fontWeight: '600',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          if (!isDragging) {
                            e.target.style.borderColor = '#16A34A';
                            e.target.style.background = '#F0FDF4';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!isDragging) {
                            e.target.style.borderColor = '#BBF7D0';
                            e.target.style.background = '#FFFFFF';
                          }
                        }}
                      >
                        Mietvertrag auswählen
                      </button>
                      <p style={{ 
                        marginTop: '8px', 
                        fontSize: '0.875rem', 
                        color: '#6B7280',
                        textAlign: 'left'
                      }}>
                        PDF, PNG, JPG oder HEIC
                      </p>
                    </div>
                  )}
                  <input
                    ref={contractInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleContractFileAdd}
                    accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,image/*"
                    multiple
                  />
                </div>
              </div>
            )}

            {/* Questions Section */}
            {schemaData?.questions && schemaData.questions.length > 0 && (
              <div style={{ 
                background: '#F0F9FF', 
                border: '2px solid #BAE6FD', 
                padding: '24px', 
                borderRadius: '12px',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{ 
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#0369A1',
                    background: '#FFFFFF',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {schemaData?.missing_contract ? '2' : '1'}
                  </div>
                  <h3 style={{ 
                    margin: 0, 
                    color: '#0C4A6E', 
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    textAlign: 'left'
                  }}>
                    Offene Fragen beantworten
                  </h3>
                </div>
                <div style={{ 
                  color: '#0C4A6E', 
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  marginBottom: '20px',
                  marginLeft: '44px',
                  textAlign: 'left'
                }}>
                  Bitte beantworten Sie die folgenden Fragen, damit die Prüfung fortgesetzt werden kann.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {schemaData.questions.map((question, index) => (
                    <div key={question.question_key || index}>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        color: '#374151',
                        fontSize: '1rem',
                        fontWeight: '500',
                        textAlign: 'left'
                      }}>
                        {question.question}
                      </label>
                      <input
                        type="text"
                        value={answers[question.question_key] || ''}
                        onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          color: '#111827',
                          boxSizing: 'border-box'
                        }}
                        placeholder="Ihre Antwort..."
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || uploadingContract}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: isSubmitting || uploadingContract ? '#9CA3AF' : 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                color: 'white',
                border: 'none',
                padding: '18px 32px',
                borderRadius: '16px',
                cursor: isSubmitting || uploadingContract ? 'not-allowed' : 'pointer',
                fontSize: '18px',
                fontWeight: 700,
                transition: 'all 0.2s ease',
                boxShadow: isSubmitting || uploadingContract ? 'none' : '0 14px 28px rgba(22, 163, 74, 0.22)',
                outline: 'none'
              }}
              onMouseOver={(e) => {
                if (!isSubmitting && !uploadingContract) {
                  e.target.style.background = 'linear-gradient(135deg, #15803d 0%, #166534 100%)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 18px 32px rgba(22, 163, 74, 0.28)';
                }
              }}
              onMouseOut={(e) => {
                if (!isSubmitting && !uploadingContract) {
                  e.target.style.background = 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 14px 28px rgba(22, 163, 74, 0.22)';
                }
              }}
            >
              {uploadingContract ? 'Lade Mietvertrag hoch...' : isSubmitting ? 'Wird übermittelt...' : 'Informationen übermitteln'}
            </button>
          </div>
        </div>
      </div>
      <div style={{ width: '100%', background: '#F3F4F6', borderTop: '1px solid #E5E7EB', padding: '16px 24px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <a href={AGB_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>AGB</a>
            <span style={{ color: '#9CA3AF' }}>|</span>
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Datenschutzbestimmungen</a>
            <span style={{ color: '#9CA3AF' }}>|</span>
            <a href={'https://www.nebenkostenprofi.de'} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Startseite</a>
          </div>
        </div>
      </div>
    </>
  );
}

